import {
  AssetPermission,
  PermissionName
} from '../../../../../components/FlowTeams/types/asset-permissions.types'
import { AssetPermissionsTableProps } from '../AssetPermissionsTable'
import { Button, ButtonProps } from '@mui/material'
import {
  DROPDOWN_ITEM_LABELS,
  DropdownItem,
  OnNewItemSelected,
  PermissionsDropdownState,
  SUBDOMAIN_DROPDOWN_ITEM_LABELS,
  SubdomainDropdownItem
} from './shared.types'
import { DropdownMenu, MobileDropdownButton } from '../../Dropdown/Dropdown.components'
import { DropdownMenuItem } from './DropdownMenuItem'
import { getDropdownMenuProps, getMobileDropdownButtonProps } from '../../Dropdown/Dropdown.utils'
import { theme } from '@dtx-company/shared-components/src'
import { useAssetPermissionsState } from '../../../../../components/FlowTeams/hooks/asset-permissions/useAssetPermissionsState'
import { useDropdownState } from '../../Dropdown/Dropdown.hooks'
import { useShowMobileStyles } from '../../../../../components/FlowTeams/FlowTeams.hooks'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import React, { MouseEvent, useCallback, useEffect, useRef } from 'react'

export const PermissionsDropdownMenu: React.FC<PermissionsDropdownMenuProps> = (
  props
): JSX.Element => {
  const state = usePermissionsDropdownState(props)
  return state.showMobileStyles && !props.disabled ? (
    <MobilePermissionsDropdown dropdownState={state} isWhitelabel={props.isWhitelabel} />
  ) : (
    <DesktopPermissionsDropdown dropdownState={state} isWhitelabel={props.isWhitelabel} />
  )
}

const MobilePermissionsDropdown: React.FC<{
  dropdownState: PermissionsDropdownState
  isWhitelabel?: boolean
}> = ({ dropdownState, isWhitelabel }) => {
  if (isWhitelabel) {
    return (
      <>
        <MobileDropdownButton {...getMobileDropdownButtonProps(dropdownState)} />
        <DropdownMenu {...getDropdownMenuProps(dropdownState)}>
          {dropdownState.whiteLabelItemsShown.map(item => (
            <DropdownMenuItem
              key={item}
              item={DropdownItem.CREATOR}
              subdomainItem={item}
              dropdownState={dropdownState}
            />
          ))}
        </DropdownMenu>
      </>
    )
  } else {
    return (
      <>
        <MobileDropdownButton {...getMobileDropdownButtonProps(dropdownState)} />
        <DropdownMenu {...getDropdownMenuProps(dropdownState)}>
          {dropdownState.itemsShown.map(item => (
            <DropdownMenuItem key={item} item={item} dropdownState={dropdownState} />
          ))}
        </DropdownMenu>
      </>
    )
  }
}

const DesktopDropdownButtonIcon: React.FC<{ dropdownIsOpen: boolean }> = ({ dropdownIsOpen }) => {
  return (
    <>
      {dropdownIsOpen ? (
        <KeyboardArrowUpIcon fontSize="large" />
      ) : (
        <KeyboardArrowDownIcon fontSize="large" />
      )}
    </>
  )
}

const DesktopPermissionsDropdown: React.FC<{
  dropdownState: PermissionsDropdownState
  focusButtonOnNextRender?: boolean
  isWhitelabel?: boolean
}> = ({ dropdownState, focusButtonOnNextRender, isWhitelabel }) => {
  const {
    buttonRef: { current: button }
  } = dropdownState

  useEffect(() => {
    if (focusButtonOnNextRender && button) {
      button?.focus()
    }
  }, [focusButtonOnNextRender, button])
  return (
    <>
      <Button {...getButtonProps(dropdownState)}>
        {isWhitelabel
          ? SUBDOMAIN_DROPDOWN_ITEM_LABELS[dropdownState.whiteLabelItemSelected]
          : DROPDOWN_ITEM_LABELS[dropdownState.itemSelected]}
      </Button>
      <DropdownMenu {...getDropdownMenuProps(dropdownState)}>
        {isWhitelabel &&
          dropdownState.whiteLabelItemsShown.map(item => (
            <DropdownMenuItem
              key={item}
              item={DropdownItem.CREATOR}
              subdomainItem={item}
              dropdownState={dropdownState}
            />
          ))}
        {!isWhitelabel &&
          dropdownState.itemsShown.map(item => (
            <DropdownMenuItem key={item} item={item} dropdownState={dropdownState} />
          ))}
      </DropdownMenu>
    </>
  )
}

export function usePermissionsDropdownState(
  props: PermissionsDropdownMenuProps
): PermissionsDropdownState {
  const { assetId, permission, isWhitelabel, isCodeSubdomain } = props
  const trackingClickPermission = props?.trackingClickPermission
  const dropdownState = useDropdownState({
    menuId: getMenuId(permission),
    buttonId: getButtonId(permission),
    disabled: props.disabled
  })

  const { closeDropdown, isOpen, hasOpened } = dropdownState

  const showMobileStyles = useShowMobileStyles()
  const assetPermissionsState = useAssetPermissionsState(assetId, isWhitelabel, isCodeSubdomain)
  const onNewItemSelected = useCallback<OnNewItemSelected>(
    item => {
      if (isWhitelabel) {
        if (item === SubdomainDropdownItem.REMOVE) {
          assetPermissionsState?.removeDraftPermission(props.permission)
        } else {
          assetPermissionsState?.updateDraftPermission({
            ...props.permission,
            permissionName:
              PERMISSION_NAMES_FOR_SUBDOMAIN_DROPDOWN_ITEMS[item as SubdomainDropdownItem]
          })
        }
      } else {
        if (item === DropdownItem.REMOVE) {
          assetPermissionsState?.removeDraftPermission(props.permission)
        } else {
          assetPermissionsState?.updateDraftPermission({
            ...props.permission,
            permissionName: PERMISSION_NAMES_FOR_DROPDOWN_ITEMS[item as DropdownItem]
          })
        }
      }
      trackingClickPermission?.(
        PERMISSION_NAMES_FOR_DROPDOWN_ITEMS[item as DropdownItem],
        props.assetId
      )
      closeDropdown()
    },
    [
      isWhitelabel,
      trackingClickPermission,
      props.assetId,
      props.permission,
      closeDropdown,
      assetPermissionsState
    ]
  )

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen && hasOpened && buttonRef.current) {
      buttonRef.current.focus()
    }
  }, [hasOpened, isOpen, buttonRef])

  const itemSelected = DROPDOWN_ITEMS_FOR_PERMISSION_NAMES[permission.permissionName]
  const whiteLabelItemSelected =
    SUBDOMAIN_DROPDOWN_ITEMS_FOR_PERMISSION_NAMES[permission.permissionName]
  return {
    showMobileStyles,
    onNewItemSelected,
    itemSelected,
    itemsShown: DROPDOWN_ITEMS.filter(
      i => i !== DropdownItem.CREATOR && i !== DropdownItem.OWNER
    ).filter(i => (props.hideRemoveItem ? i !== DropdownItem.REMOVE : true)),
    ...dropdownState,
    whiteLabelItemSelected,
    whiteLabelItemsShown: SUBDOMAIN_DROPDOWN_ITEMS,
    assetId,
    trackingClickPermissionDropdown: props.trackingClickPermissionDropdown
  }
}

export interface PermissionsDropdownMenuProps
  extends Pick<
    AssetPermissionsTableProps,
    'trackingClickPermission' | 'trackingClickPermissionDropdown' | 'assetId'
  > {
  disabled: boolean
  permission: AssetPermission
  hideRemoveItem?: boolean
  isWhitelabel?: boolean
  isCodeSubdomain?: boolean
}

function getMenuId(p: AssetPermission): string {
  return `permissions-role-dropdown-${p.entityId ?? ''}`
}
function getButtonId(p: AssetPermission): string {
  return `permissions-role-dropdown-button-${p.entityId ?? ''}`
}

function getButtonProps(dropdownState: PermissionsDropdownState): ButtonProps {
  return {
    ref: dropdownState.buttonRef,
    id: DROPDOWN_MENU_BUTTON_ID,
    'aria-controls': dropdownState.isOpen ? DROPDOWN_MENU_ID : undefined,
    'aria-haspopup': 'true',
    'aria-expanded': dropdownState.isOpen ? 'true' : undefined,
    color: 'primary',
    tabIndex: dropdownState.disabled ? -1 : 0,
    disableRipple: true,
    sx: {
      fontSize: '14px',
      textTransform: 'none',
      border: 'none',
      '&:hover': {
        border: 'none',
        cursor: !dropdownState.disabled ? 'pointer' : 'auto',
        backgroundColor: dropdownState.disabled ? 'white' : 'inherit'
      },
      '&:focus': {
        backgroundColor: !dropdownState.disabled ? theme.colors.secondary.border : 'white'
      }
    },
    onClick: (e: MouseEvent<HTMLElement>) => {
      dropdownState.toggleDropdown(e.currentTarget)
      dropdownState.trackingClickPermissionDropdown?.(dropdownState.assetId)
    },
    variant: 'outlined',
    endIcon: !dropdownState.disabled ? (
      <DesktopDropdownButtonIcon dropdownIsOpen={dropdownState.isOpen} />
    ) : null
  }
}

const DROPDOWN_MENU_ID = 'permissions-dropdown-menu'
const DROPDOWN_MENU_BUTTON_ID = 'permissions-dropdown-button'

export const DROPDOWN_ITEMS: DropdownItem[] = [
  DropdownItem.CREATOR,
  DropdownItem.EDITOR,
  DropdownItem.VIEWER,
  DropdownItem.REMOVE
]

export const DROPDOWN_ITEMS_FOR_PERMISSION_NAMES: Record<PermissionName, DropdownItem> = {
  [PermissionName.CREATOR]: DropdownItem.CREATOR,
  [PermissionName.OWNER]: DropdownItem.CREATOR,
  [PermissionName.EDITOR]: DropdownItem.EDITOR,
  [PermissionName.VIEWER]: DropdownItem.VIEWER
}

export const PERMISSION_NAMES_FOR_DROPDOWN_ITEMS: Record<DropdownItem, PermissionName> = {
  [DropdownItem.CREATOR]: PermissionName.CREATOR,
  [DropdownItem.OWNER]: PermissionName.CREATOR,
  [DropdownItem.EDITOR]: PermissionName.EDITOR,
  [DropdownItem.VIEWER]: PermissionName.VIEWER,
  [DropdownItem.REMOVE]: PermissionName.CREATOR //stub, not really used
}

export const SUBDOMAIN_DROPDOWN_ITEMS: SubdomainDropdownItem[] = [
  SubdomainDropdownItem.SHARED,
  SubdomainDropdownItem.REMOVE
]

export const SUBDOMAIN_DROPDOWN_ITEMS_FOR_PERMISSION_NAMES: Record<
  PermissionName,
  SubdomainDropdownItem
> = {
  [PermissionName.CREATOR]: SubdomainDropdownItem.SHARED,
  [PermissionName.OWNER]: SubdomainDropdownItem.SHARED,
  [PermissionName.EDITOR]: SubdomainDropdownItem.SHARED,
  [PermissionName.VIEWER]: SubdomainDropdownItem.SHARED
}

export const PERMISSION_NAMES_FOR_SUBDOMAIN_DROPDOWN_ITEMS: Record<
  SubdomainDropdownItem,
  PermissionName
> = {
  [SubdomainDropdownItem.SHARED]: PermissionName.VIEWER,
  [SubdomainDropdownItem.REMOVE]: PermissionName.CREATOR //stub, not really used
}
